import { ApiError as ContractsApiError } from '@adverity/contracts'
import * as Sentry from '@sentry/react'
import type { AxiosError } from 'axios'
import axios from 'axios'
import Cookies from 'js-cookie'

const csrftoken = Cookies.get('csrftoken')
const INVALID_PAGE_REGEX = /invalid page/i

export type ApiError<T = unknown> = AxiosError<T>

export const isApiError = <T = unknown>(error: unknown): error is ApiError<T> => !!error && axios.isAxiosError(error)

export const isInvalidPageError = (error: unknown) =>
    isApiError<{ detail: string }>(error) &&
    error.response?.status === 404 &&
    INVALID_PAGE_REGEX.test(error.response.data.detail)

/** @public we have tests for this */
export const paramsSerializer = (params: Record<string, string | Array<string>>) => {
    if ('queryString' in params && typeof params.queryString === 'string') {
        return params.queryString
    }

    const paramsNormalized = Object.entries(params).flatMap(([key, value]) => {
        if (Array.isArray(value)) {
            return value.map((singleValue) => [key, singleValue])
        }

        return [[key, value]]
    })

    return new URLSearchParams(paramsNormalized).toString()
}

export const retryWhenServerError = (failureCount: number, error: unknown): boolean => {
    let status: number | undefined

    if (isApiError(error)) {
        status = error.response?.status
    }

    if (error instanceof ContractsApiError) {
        status = error.status
    }

    return failureCount < 3 && (!status || status >= 500)
}

/**
 * The only used API approach in the app.
 */
const client = axios.create({
    withCredentials: true,
    headers: csrftoken ? { 'X-CSRFToken': csrftoken } : undefined,
    paramsSerializer,
})

client.interceptors.request.use((config) => {
    const currentSpan = Sentry.getActiveSpan() ?? Sentry.startInactiveSpan({ name: 'Manual Span' })
    const sentryTraceHeader = Sentry.spanToTraceHeader(currentSpan)
    const sentryBaggageHeader = Sentry.spanToBaggageHeader(currentSpan)

    const newConfig = {
        ...config,
        headers: {
            ...config.headers,
            ...(sentryTraceHeader ? { 'sentry-trace': sentryTraceHeader } : {}),
            ...(sentryBaggageHeader ? { baggage: sentryBaggageHeader } : {}),
        },
    }

    return newConfig
})

export default client
